import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { GLYPHS, SymbolBadge } from "@/components/SafetySymbols";
import {
  setActiveLehrbildWorld,
  setLehrbildPrintKontext,
  LehrbildImage,
  useLehrbildImage,
  _debugGetManifestMeta,
} from "@/vsi/lehrbild";
import { buildRichtigFalsch } from "@/vsi/richtigFalsch";
import { createT, MONTEUR_DICTS } from "@/i18n/monteur";
import {
  getDinProfile,
  DIN_FORMATS,
  ORIENTATIONS,
  MM_TO_PX,
} from "@/print/dinProfiles";
import {
  ArrowLeft,
  Printer,
  Download,
  Loader2,
  AlertTriangle,
  TriangleAlert,
  ShieldCheck,
  Check,
  X,
  ImageOff,
  Info,
} from "lucide-react";

// CLARTEXT — AP-PLAKAT-UX (Juni 2026): vollständige visuelle Unterweisungs-Ausgabe.
// Das Plakat ist eine Darstellungsschicht für Aushang an Maschine/Gefahrenstelle.
// Es zeigt ALLE vorhandenen Lehrbilder, PSA und Sicherheitssymbole VOLLSTÄNDIG in
// fester didaktischer Reihenfolge (Kopf → PSA → Symbole → Gefahren → Richtig →
// Falsch). Passt nicht alles auf eine Seite, werden automatisch weitere A4-Seiten
// erzeugt — KEIN Abschneiden, KEINE versteckten Inhalte. Gruppen, die umbrechen,
// erhalten auf der Folgeseite eine Fortsetzungsüberschrift; Bilder werden über alle
// Seiten fortlaufend nummeriert. STRIKT beweisneutral & nur Druckansicht: berührt
// KEINE Beweiskette/WORM/Nachweise/PDF/content_hash/Bildpipeline/S3/Übersetzungen.
// Fachtexte aus `display`, Symbol-Labels aus dem VLO, UI-Hülsen aus t().

const LANG_LABELS = {
  de: "Deutsch",
  en: "English",
  pl: "Polski",
  ro: "Română",
  tr: "Türkçe",
  uk: "Українська",
  bg: "Български",
  hr: "Hrvatski",
  ru: "Русский",
};
const AVAILABLE_LANGS = Object.keys(MONTEUR_DICTS);

// --- Paginator: verteilt Blöcke/Kartenreihen auf A4-Seiten (kein Abschneiden) ----
function buildPages(model, h, profile) {
  const contentH = (profile.heightMm - 2 * profile.marginMm) * MM_TO_PX;
  const blockGap = 6 * MM_TO_PX;
  const rowGap = 4 * MM_TO_PX;
  const safety = 16;
  const availOf = (no) =>
    contentH - h.footer - (no === 1 ? 0 : h.runHeader) - safety;

  const pages = [];
  let page = { number: 1, blocks: [] };
  let used = 0;
  const flush = () => {
    pages.push(page);
    page = { number: pages.length + 1, blocks: [] };
    used = 0;
  };

  const placeAtomic = (type, height) => {
    if (used > 0 && used + blockGap + height > availOf(page.number)) flush();
    page.blocks.push({ type });
    used += (used > 0 ? blockGap : 0) + height;
  };

  // Kopfbereich immer zuerst (Seite 1).
  page.blocks.push({ type: "kopf" });
  used = h.kopf;
  if (model.psaItems.length) placeAtomic("psa", h.psa);
  if (model.symbols.length) placeAtomic("symbols", h.symbols);

  for (const g of model.groups) {
    const rows = [];
    for (let i = 0; i < g.cards.length; i += model.cols)
      rows.push(g.cards.slice(i, i + model.cols));
    let idx = 0;
    let firstPortion = true;
    while (idx < rows.length) {
      // Überschrift + erste Reihe zusammenhalten (kein verwaistes Heading).
      if (
        used > 0 &&
        used + blockGap + h.heading + rowGap + h.card > availOf(page.number)
      )
        flush();
      const block = {
        type: "group",
        groupKey: g.key,
        title: g.title,
        sub: g.sub,
        tone: g.tone,
        headingMode: firstPortion ? "full" : "cont",
        cards: [],
      };
      page.blocks.push(block);
      used += (used > 0 ? blockGap : 0) + h.heading;
      while (idx < rows.length) {
        if (used + rowGap + h.card > availOf(page.number)) break;
        for (const c of rows[idx]) block.cards.push(c);
        used += rowGap + h.card;
        idx++;
      }
      if (idx < rows.length) {
        flush();
        firstPortion = false;
      } else {
        break;
      }
    }
  }
  pages.push(page);
  return pages;
}

function groupTestId(key) {
  return key === "hazards"
    ? "hazard-markers"
    : key === "richtig"
    ? "richtig-falsch-richtig"
    : "richtig-falsch-falsch";
}

function PosterCard({ card }) {
  const tone = card.tone;
  const ToneIcon =
    card.kind === "richtig" ? Check : card.kind === "falsch" ? X : TriangleAlert;
  return (
    <div
      className={`poster-card poster-card-${tone}`}
      data-testid={card.testId}
      data-number={card.number}
    >
      <div className="poster-card-imgbox">
        <LehrbildImage
          text={card.text}
          lookupText={card.lookupText}
          className="poster-card-img-inner"
          testId={`${card.testId}-lehrbild`}
          fallback={
            <div className="poster-card-imgph">
              <ImageOff style={{ width: "8mm", height: "8mm", opacity: 0.35 }} />
            </div>
          }
        />
      </div>
      <div className="poster-card-cap">
        <span className={`poster-card-num poster-num-${tone}`}>
          <ToneIcon style={{ width: "3mm", height: "3mm" }} />
          {card.number}
        </span>
        <div className="poster-card-texts">
          {card.category ? (
            <span className="poster-card-cat">{card.category}</span>
          ) : null}
          <span className="poster-card-text">{card.text}</span>
        </div>
      </div>
    </div>
  );
}

// ADS-AUTOPREVIEW 01 · Stufe 4.3A: neutrale Sperransicht ohne Plakat-Bezug; Rückweg nur zu einer lesbaren Welt.
function NichtVerfuegbar({ rueckweg, onZurueck }) {
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center gap-4 text-center px-6"
      data-testid="seite-nicht-verfuegbar"
    >
      <p className="text-slate-700" data-testid="seite-nicht-verfuegbar-text">
        Diese Seite ist nicht verfügbar.
      </p>
      {rueckweg && (
        <Button variant="outline" onClick={onZurueck} data-testid="seite-nicht-verfuegbar-zurueck">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück zur Welt
        </Button>
      )}
    </div>
  );
}

export default function WeltPlakat({ druckansicht = false }) {
  const { id, token } = useParams();
  const navigate = useNavigate();
  const [german, setGerman] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [format, setFormat] = useState("a4");
  const [orientation, setOrientation] = useState("portrait");
  const [lang, setLang] = useState("de");
  const [display, setDisplay] = useState(null);
  const [servedLang, setServedLang] = useState("de");
  const [pages, setPages] = useState([]);
  // PLAKAT-PDF (2026-06): Zustand des serverseitigen Chromium-Downloads.
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  // Stufe 4.3A: serverseitige Plakat-Berechtigung („pruefen" → „erlaubt" | „gesperrt").
  const [zugang, setZugang] = useState(druckansicht ? "erlaubt" : "pruefen");
  const [rueckweg, setRueckweg] = useState(false);

  const measRefs = {
    kopf: useRef(null),
    psa: useRef(null),
    symbols: useRef(null),
    heading: useRef(null),
    card: useRef(null),
    footer: useRef(null),
    runHeader: useRef(null),
  };

  // IP2-D2 · Print-Renderkontext: das Unterweisungsplakat ist ein
  // Druckprodukt (Browserdruck via window.print) und verwendet daher
  // Print-Derivate (sonst Master-Fallback). Mount/Unmount-Muster exakt
  // analog IP2-D1C/E1C — beim Zurücknavigieren wird der Kontext gelöscht.
  useEffect(() => {
    setLehrbildPrintKontext(true);
    return () => setLehrbildPrintKontext(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (druckansicht) {
          // PLAKAT-PDF (2026-06): interner Chromium-Renderweg — Daten
          // AUSSCHLIESSLICH über das kurzlebige, zweckgebundene Render-Token
          // (Muster 8H); Format/Ausrichtung/Sprache kommen eingefroren mit.
          const res = await api.getPlakatDruckDaten(token);
          if (!cancelled) {
            setGerman(res.course);
            setActiveLehrbildWorld(res.course.id);
            setFormat(res.format);
            setOrientation(res.orientation);
            setLang(res.lang);
            if (res.plakat_display) {
              setDisplay(res.plakat_display.display || null);
              setServedLang(res.plakat_display.served_language || "de");
            }
          }
        } else {
          try {
            await api.getPlakatDisplay(id, "de");
          } catch {
            const lesbar = await api.getCourse(id).then(() => true, () => false);
            if (!cancelled) {
              setRueckweg(lesbar);
              setZugang("gesperrt");
            }
            return;
          }
          if (!cancelled) setZugang("erlaubt");
          const c = await api.getCourse(id);
          if (!cancelled) {
            setGerman(c);
            setActiveLehrbildWorld(c.id);
          }
        }
      } catch (e) {
        if (!cancelled)
          setError(
            e?.response?.data?.detail || "Plakat konnte nicht geladen werden."
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, token, druckansicht]);

  useEffect(() => {
    // PLAKAT-PDF (2026-06): die Druckroute erhält `display` eingefroren über
    // das Render-Token — der Owner-Display-Fetch darf dort NIE laufen.
    if (druckansicht) return undefined;
    if (lang === "de") {
      setDisplay(null);
      setServedLang("de");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getPlakatDisplay(id, lang);
        if (!cancelled) {
          setDisplay(res?.display || null);
          setServedLang(res?.served_language || "de");
        }
      } catch {
        if (!cancelled) {
          setDisplay(null);
          setServedLang("de");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, lang, druckansicht]);

  // PLAKAT-PDF · LIVE-L4-Muster: Re-Render-Abo auf die Lehrbild-Runtime +
  // DOM-Marker „Manifest-Fetch abgeschlossen" für die Chromium-Wartekante
  // (auch bei leerem/fehlendem Manifest "1" — kein Deadlock).
  useLehrbildImage("", "");
  const lehrbildMeta = german?.id ? _debugGetManifestMeta(german.id) : null;
  const lehrbildManifestGeladen = !german?.id || !!lehrbildMeta;

  // PLAKAT-PDF (2026-06): serverseitiger Chromium-Download — exakt die
  // aktuelle Toolbar-Auswahl (Format/Ausrichtung/Sprache); KEIN window.print().
  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const res = await api.downloadPlakatPdf(id, { format, orientation, lang });
      const cd = res.headers?.["content-disposition"] || "";
      const match = cd.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : "CLARTEXT-Plakat.pdf";
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      setDownloadError(
        "Die PDF konnte nicht erstellt werden. Bitte versuchen Sie es erneut."
      );
    } finally {
      setDownloading(false);
    }
  };

  // --- abgeleitete Daten (immer aus genau einer Quelle) -----------------------
  const data = lang === "de" ? german : display || german;
  const effectiveLang = lang === "de" ? "de" : servedLang;
  const fallbackToGerman = lang !== "de" && servedLang === "de";
  const t = createT(effectiveLang);
  const profile = getDinProfile(format, orientation);
  const cols = orientation === "landscape" ? 3 : 2;

  const vo = data && Array.isArray(data.visual_objects) ? data.visual_objects : [];
  const byType = (ty) => vo.find((o) => o && o.vlo_type === ty);
  const symbols = (byType("safety_symbols")?.payload?.symbols || []).filter(
    (s) => s && GLYPHS[s.id]
  );
  const psaItems = byType("psa_summary")?.payload?.items || [];
  const hazards = (byType("hazard_markers")?.payload?.items || []).filter(
    (it) =>
      it &&
      ((typeof it.category === "string" && it.category.trim()) ||
        (typeof it.description === "string" && it.description.trim()))
  );
  const rf = data ? buildRichtigFalsch(data) : null;
  const rfRichtig = rf?.payload?.richtig || [];
  const rfFalsch = rf?.payload?.falsch || [];

  // AP-LANG-SOURCE: sprachneutraler Lehrbild-Lookup über den deutschen Quelltext.
  // `german` (Originalwelt) ist hier ohnehin parallel geladen; Anzeige bleibt übersetzt.
  const germanVO =
    german && Array.isArray(german.visual_objects) ? german.visual_objects : [];
  const germanHazardById = {};
  ((germanVO.find((o) => o && o.vlo_type === "hazard_markers")?.payload?.items) || []).forEach(
    (h) => {
      if (h && h.id) germanHazardById[h.id] = h.description || "";
    }
  );
  const germanRf = german ? buildRichtigFalsch(german) : null;
  const germanRfRichtig = germanRf?.payload?.richtig || [];
  const germanRfFalsch = germanRf?.payload?.falsch || [];

  // Bildgruppen in verbindlicher didaktischer Reihenfolge + fortlaufende Nummern.
  const groups = [];
  if (hazards.length)
    groups.push({
      key: "hazards",
      title: t("plakat.hazards"),
      sub: t("plakat.hazardsSub"),
      tone: "amber",
      cards: hazards.map((it) => ({
        id: it.id,
        testId: `hazard-marker-${it.id}`,
        kind: "hazard",
        tone: "amber",
        category: typeof it.category === "string" ? it.category.trim() : "",
        text: it.description || "",
        lookupText: germanHazardById[it.id] || "",
      })),
    });
  if (rfRichtig.length)
    groups.push({
      key: "richtig",
      title: t("plakat.correct"),
      sub: t("plakat.correctSub"),
      tone: "green",
      cards: rfRichtig.map((r, i) => ({
        id: `r${i}`,
        testId: `richtig-falsch-richtig-card-${i}`,
        kind: "richtig",
        tone: "green",
        category: "",
        text: r.text || "",
        lookupText: germanRfRichtig[i]?.text || "",
      })),
    });
  if (rfFalsch.length)
    groups.push({
      key: "falsch",
      title: t("plakat.wrong"),
      sub: t("plakat.wrongSub"),
      tone: "rose",
      cards: rfFalsch.map((r, i) => ({
        id: `f${i}`,
        testId: `richtig-falsch-falsch-card-${i}`,
        kind: "falsch",
        tone: "rose",
        category: "",
        text: r.text || "",
        lookupText: germanRfFalsch[i]?.text || "",
      })),
    });
  let counter = 0;
  groups.forEach((g) => g.cards.forEach((c) => (c.number = ++counter)));
  const totalImages = counter;

  const qrBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  const qrTarget = `${qrBase}/monteur/login?welt=${(data && data.id) || id}`;
  const qrPx = Math.round((profile.typo.qr || 116) * 0.78);

  const typoVars = {
    "--p-title": `${profile.typo.title}px`,
    "--p-h2": `${profile.typo.h2}px`,
    "--p-body": `${profile.typo.body}px`,
    "--p-small": `${profile.typo.small}px`,
  };

  const sig = data
    ? `${effectiveLang}|${format}|${orientation}|${data.id || id}|h${hazards.length}|r${rfRichtig.length}|f${rfFalsch.length}|p${psaItems.length}|s${symbols.length}`
    : "";

  // --- Render-Knoten (auch im Messcontainer verwendet) ------------------------
  const kopfNode = (
    <div className="poster-kopf" data-testid="plakat-kopf">
      <div className="poster-kopf-main">
        <div className="poster-eyebrow">{t("plakat.eyebrow")}</div>
        <h1 className="p-title" data-testid="plakat-title">
          {(data && data.title) || ""}
        </h1>
        <p className="poster-subtitle" data-testid="plakat-subtitle">
          {t("plakat.subtitle")}
        </p>
      </div>
      <div className="poster-kopf-qr" data-testid="plakat-qr-access">
        <div className="poster-qr-box">
          <QRCodeSVG
            value={qrTarget}
            size={qrPx}
            level="M"
            marginSize={0}
            data-testid="plakat-qr-code"
          />
        </div>
        <span className="poster-qr-hint">{t("plakat.qrText")}</span>
      </div>
    </div>
  );

  const psaNode = (
    <section className="poster-block poster-psa" data-testid="psa-summary">
      <div className="poster-block-head">
        <ShieldCheck style={{ width: "1em", height: "1em", color: "#0284c7" }} />
        <h2 className="p-h2">{t("plakat.psa")}</h2>
      </div>
      <div className="poster-chip-row">
        {psaItems.map((it) => (
          <span
            key={it.id}
            className="poster-psa-chip"
            data-testid={`psa-summary-item-${it.id}`}
          >
            <ShieldCheck style={{ width: "3.5mm", height: "3.5mm", color: "#0284c7" }} />
            {it.label}
          </span>
        ))}
      </div>
    </section>
  );

  const symbolsNode = (
    <section className="poster-block poster-symbols" data-testid="plakat-symbols">
      <div className="poster-block-head">
        <TriangleAlert style={{ width: "1em", height: "1em", color: "#475569" }} />
        <h2 className="p-h2">{t("plakat.symbols")}</h2>
      </div>
      <div className="poster-symbol-grid">
        {symbols.map((s) => {
          const Icon = GLYPHS[s.id];
          return (
            <div
              key={s.id}
              className="poster-symbol-chip"
              data-testid={`plakat-symbol-${s.id}`}
            >
              <SymbolBadge category={s.category} Icon={Icon} />
              <span className="poster-symbol-label">{s.label}</span>
            </div>
          );
        })}
      </div>
    </section>
  );

  const renderGroupBlock = (block, keySuffix) => (
    <section
      className={`poster-group poster-group-${block.tone}`}
      data-testid={groupTestId(block.groupKey)}
      data-group={block.groupKey}
      data-heading-mode={block.headingMode}
      key={keySuffix}
    >
      <div
        className="poster-group-head"
        data-testid={`plakat-group-heading-${block.groupKey}`}
        data-heading-mode={block.headingMode}
        data-group={block.groupKey}
      >
        <h2 className="p-h2">
          {block.title}
          {block.headingMode === "cont" ? (
            <span className="poster-cont" data-testid="plakat-group-cont">
              {" "}
              ({t("plakat.continued")})
            </span>
          ) : null}
        </h2>
        {block.headingMode === "full" ? (
          <span className="poster-group-sub">{block.sub}</span>
        ) : null}
      </div>
      <div className="poster-cards" style={{ "--cols": cols }}>
        {block.cards.map((c) => (
          <PosterCard key={c.id} card={c} />
        ))}
      </div>
    </section>
  );

  const footerNode = (n, total) => (
    <div
      className="poster-footer"
      data-testid={n === 1 ? "plakat-footer-hinweis" : `plakat-footer-${n}`}
    >
      <span className="poster-footer-hint">{t("plakat.footerHint")}</span>
      <span className="poster-footer-page" data-testid={`plakat-page-number-${n}`}>
        {t("plakat.page")} {n} / {total}
      </span>
    </div>
  );

  const runHeaderNode = (
    <div className="poster-runheader">
      <span className="poster-runheader-title">{(data && data.title) || ""}</span>
      <span className="poster-runheader-eyebrow">{t("plakat.eyebrow")}</span>
    </div>
  );

  // --- Messung + Pagination ---------------------------------------------------
  useLayoutEffect(() => {
    if (!sig) return;
    const px = (ref) => (ref.current ? ref.current.offsetHeight : 0);
    const h = {
      kopf: px(measRefs.kopf),
      psa: px(measRefs.psa),
      symbols: px(measRefs.symbols),
      heading: px(measRefs.heading),
      card: px(measRefs.card),
      footer: px(measRefs.footer),
      runHeader: px(measRefs.runHeader),
    };
    if (!h.card || !h.heading) {
      setPages([]);
      return;
    }
    const model = { psaItems, symbols, groups, cols };
    setPages(buildPages(model, h, profile));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  if (zugang === "gesperrt") {
    return (
      <NichtVerfuegbar
        rueckweg={rueckweg}
        onZurueck={() => navigate(`/unternehmer/welt/${id}`)}
      />
    );
  }

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-slate-500"
        data-testid="plakat-loading"
      >
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        {zugang === "erlaubt" && "Plakat wird geladen…"}
      </div>
    );
  }

  if (error || !german) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center gap-4 text-center px-6"
        data-testid="plakat-error"
      >
        <AlertTriangle className="w-10 h-10 text-amber-500" />
        <p className="text-slate-700">{error || "Welt nicht gefunden."}</p>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>
      </div>
    );
  }

  const totalPages = pages.length;

  return (
    <div className="plakat-root min-h-screen" data-testid="plakat-page">
      {/* Owner-Werkzeugleiste – nur am Bildschirm, deutsch (Owner-UI).
         PLAKAT-PDF: in der internen Chromium-Druckroute nie gerendert. */}
      {!druckansicht && (
      <div className="plakat-toolbar" data-testid="plakat-toolbar">
        <div className="plakat-toolbar-inner">
          <button
            onClick={() => navigate(`/unternehmer/welt/${id}`)}
            data-testid="plakat-back-button"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Welt
          </button>
          <div className="flex items-center gap-2">
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              data-testid="plakat-format-select"
              className="plakat-select"
            >
              {Object.values(DIN_FORMATS).map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </select>
            <select
              value={orientation}
              onChange={(e) => setOrientation(e.target.value)}
              data-testid="plakat-orientation-select"
              className="plakat-select"
            >
              {Object.values(ORIENTATIONS).map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
            <select
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              data-testid="plakat-language-select"
              className="plakat-select"
            >
              {AVAILABLE_LANGS.map((l) => (
                <option key={l} value={l}>
                  {LANG_LABELS[l] || l}
                </option>
              ))}
            </select>
            <Button
              onClick={handleDownload}
              disabled={downloading}
              data-testid="plakat-download-button"
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              {downloading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              {downloading ? "PDF wird erstellt…" : "Plakat herunterladen"}
            </Button>
            <Button
              variant="outline"
              onClick={() => window.print()}
              data-testid="plakat-print-button"
            >
              <Printer className="w-4 h-4 mr-2" />
              Direkt drucken
            </Button>
          </div>
        </div>
        <div className="plakat-toolbar-info" data-testid="plakat-download-hinweis">
          Plakat herunterladen erzeugt eine optimierte PDF direkt auf dem
          Server. 'Direkt drucken' verwendet den Druckdialog Ihres Browsers und
          kann je nach Browser unterschiedlich große PDF-Dateien erzeugen.
        </div>
        <div className="plakat-toolbar-meta" data-testid="plakat-page-count">
          {totalImages} {totalImages === 1 ? "Bild" : "Bilder"} · {totalPages}{" "}
          {totalPages === 1 ? "Seite" : "Seiten"}
        </div>
        {downloadError && (
          <div className="plakat-screen-note" data-testid="plakat-download-fehler">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>{downloadError}</span>
          </div>
        )}
        {fallbackToGerman && (
          <div className="plakat-screen-note" data-testid="plakat-fallback-note">
            <Info className="w-4 h-4 shrink-0" />
            <span>
              Für „{LANG_LABELS[lang] || lang}" liegt noch keine vollständige
              Übersetzung dieser Welt vor – das Plakat wird auf Deutsch gedruckt.
            </span>
          </div>
        )}
      </div>
      )}

      {/* Bühne (Bildschirm): Seiten gestapelt. Druck: je Seite ein A4-Blatt. */}
      <div
        className="plakat-stage"
        data-testid="plakat-sheet"
        data-format={profile.format}
        data-orientation={profile.orientation}
        data-pages={totalPages}
        data-lehrbild-manifest-geladen={lehrbildManifestGeladen ? "1" : "0"}
      >
        {pages.map((p) => (
          <div
            key={p.number}
            className="poster-page"
            data-testid={`plakat-page-${p.number}`}
            style={{
              width: `${profile.widthMm}mm`,
              height: `${profile.heightMm}mm`,
              padding: `${profile.marginMm}mm`,
              ...typoVars,
            }}
          >
            {p.number > 1 ? runHeaderNode : null}
            <div className="poster-page-content">
              {p.blocks.map((b, bi) => {
                if (b.type === "kopf") return <div key={`k${bi}`}>{kopfNode}</div>;
                if (b.type === "psa") return <div key={`p${bi}`}>{psaNode}</div>;
                if (b.type === "symbols")
                  return <div key={`s${bi}`}>{symbolsNode}</div>;
                return renderGroupBlock(b, `g${bi}`);
              })}
            </div>
            {footerNode(p.number, totalPages)}
          </div>
        ))}
      </div>

      {/* Versteckter Messcontainer (Pagination) – nie sichtbar/gedruckt. */}
      <div
        className="plakat-measure"
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-100000px",
          top: 0,
          width: `${profile.safeWidthMm}mm`,
          ...typoVars,
        }}
      >
        <div ref={measRefs.kopf}>{kopfNode}</div>
        {psaItems.length > 0 ? <div ref={measRefs.psa}>{psaNode}</div> : null}
        {symbols.length > 0 ? (
          <div ref={measRefs.symbols}>{symbolsNode}</div>
        ) : null}
        <div ref={measRefs.heading}>
          {renderGroupBlock(
            {
              type: "group",
              groupKey: "hazards",
              title: t("plakat.hazards"),
              sub: t("plakat.hazardsSub"),
              tone: "amber",
              headingMode: "full",
              cards: [],
            },
            "meas-head"
          )}
        </div>
        <div className="poster-cards" style={{ "--cols": cols }}>
          <div ref={measRefs.card}>
            <PosterCard
              card={{
                id: "meas",
                testId: "meas-card",
                kind: "hazard",
                tone: "amber",
                category: "Messung",
                number: 0,
                text:
                  "Beispieltext für die Höhenmessung einer Karte mit zwei bis drei Zeilen Beschreibung.",
              }}
            />
          </div>
        </div>
        <div ref={measRefs.footer}>{footerNode(2, 2)}</div>
        <div ref={measRefs.runHeader}>{runHeaderNode}</div>
      </div>

      <style>{`
        .plakat-root { background: #e2e8f0; }
        .plakat-toolbar { position: sticky; top: 0; z-index: 10; background: rgba(255,255,255,0.96); backdrop-filter: blur(6px); border-bottom: 1px solid #e2e8f0; }
        .plakat-toolbar-inner { max-width: 1100px; margin: 0 auto; padding: 12px 16px; display: flex; align-items: center; justify-content: space-between; gap: 12px; }
        .plakat-toolbar-meta { max-width: 1100px; margin: 0 auto; padding: 0 16px 10px; font-size: 12px; color: #64748b; }
        .plakat-toolbar-info { max-width: 1100px; margin: 0 auto; padding: 0 16px 10px; font-size: 12px; color: #64748b; }
        .plakat-select { font-size: 14px; border: 1px solid #cbd5e1; border-radius: 8px; padding: 6px 10px; background: #fff; color: #0f172a; }
        .plakat-screen-note { max-width: 1100px; margin: 0 auto; padding: 0 16px 12px; display: flex; align-items: flex-start; gap: 8px; font-size: 13px; color: #92400e; }
        .plakat-stage { display: flex; flex-direction: column; align-items: center; gap: 28px; padding: 28px; overflow: auto; }

        .poster-page { box-sizing: border-box; background: #fff; color: #0f172a; box-shadow: 0 10px 30px rgba(15,23,42,0.18); display: flex; flex-direction: column; overflow: hidden; font-size: var(--p-body); line-height: 1.3; }
        .poster-page-content { flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; gap: 6mm; }
        .poster-page .p-title { font-size: var(--p-title); font-weight: 800; line-height: 1.06; margin: 0; }
        .poster-page .p-h2 { font-size: var(--p-h2); font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; margin: 0; }

        .poster-kopf { display: flex; gap: 6mm; align-items: flex-start; justify-content: space-between; border-bottom: 2px solid #0f172a; padding-bottom: 4mm; }
        .poster-kopf-main { min-width: 0; }
        .poster-eyebrow { font-size: var(--p-small); text-transform: uppercase; letter-spacing: 0.18em; color: #64748b; font-weight: 600; }
        .poster-subtitle { font-size: var(--p-small); color: #475569; margin: 1mm 0 0; }
        .poster-kopf-qr { flex-shrink: 0; display: flex; align-items: center; gap: 3mm; max-width: 70mm; }
        .poster-qr-box { border: 2px solid #cbd5e1; background: #fff; padding: 1.5mm; border-radius: 2mm; flex-shrink: 0; }
        .poster-qr-hint { font-size: var(--p-small); color: #334155; }

        .poster-block-head { display: flex; align-items: center; gap: 2mm; margin-bottom: 2.5mm; }
        .poster-psa { border: 2px solid #7dd3fc; background: #f0f9ff; border-radius: 3mm; padding: 3mm; }
        .poster-psa .p-h2 { color: #075985; }
        .poster-symbols { border: 2px solid #e2e8f0; background: #f8fafc; border-radius: 3mm; padding: 3mm; }
        .poster-chip-row { display: flex; flex-wrap: wrap; gap: 2.5mm; }
        .poster-psa-chip { display: inline-flex; align-items: center; gap: 1.5mm; border-radius: 999px; background: #fff; border: 2px solid #7dd3fc; padding: 1.5mm 3mm; font-size: var(--p-small); font-weight: 600; color: #075985; }
        .poster-symbol-grid { display: flex; flex-wrap: wrap; gap: 4mm; }
        .poster-symbol-chip { display: flex; flex-direction: column; align-items: center; text-align: center; width: 24mm; }
        .poster-symbol-label { margin-top: 1mm; font-size: var(--p-small); line-height: 1.1; color: #334155; }

        .poster-group-head { break-after: avoid; margin-bottom: 2.5mm; }
        .poster-group-sub { font-size: var(--p-small); color: #64748b; }
        .poster-group-amber .p-h2 { color: #92400e; }
        .poster-group-green .p-h2 { color: #047857; }
        .poster-group-rose .p-h2 { color: #be123c; }
        .poster-cont { font-weight: 600; text-transform: none; letter-spacing: 0; color: #94a3b8; }

        .poster-cards { display: grid; grid-template-columns: repeat(var(--cols), 1fr); gap: 4mm; }
        .poster-card { break-inside: avoid; border: 2px solid #e2e8f0; border-radius: 3mm; padding: 2.5mm; display: flex; flex-direction: column; background: #fff; }
        .poster-card-amber { border-color: #fcd34d; background: #fffbeb; }
        .poster-card-green { border-color: #6ee7b7; background: #ecfdf5; }
        .poster-card-rose { border-color: #fda4af; background: #fff1f2; }
        .poster-card-imgbox { height: 26mm; border-radius: 2mm; overflow: hidden; background: #fff; display: flex; align-items: center; justify-content: center; margin-bottom: 2mm; border: 1px solid rgba(15,23,42,0.06); }
        .poster-card-img-inner { height: 100%; width: 100%; }
        .poster-card-imgph { display: flex; align-items: center; justify-content: center; }
        .poster-card-cap { display: flex; gap: 2mm; align-items: flex-start; }
        .poster-card-num { flex-shrink: 0; display: inline-flex; align-items: center; gap: 1mm; min-width: 9mm; height: 6mm; padding: 0 1.5mm; border-radius: 999px; color: #fff; font-weight: 700; font-size: var(--p-small); }
        .poster-num-amber { background: #b45309; }
        .poster-num-green { background: #059669; }
        .poster-num-rose { background: #e11d48; }
        .poster-card-texts { min-width: 0; }
        .poster-card-cat { display: block; font-weight: 700; font-size: var(--p-small); color: #0f172a; }
        .poster-card-text { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; font-size: var(--p-small); line-height: 1.25; color: #1f2937; }

        .poster-runheader { display: flex; align-items: baseline; justify-content: space-between; border-bottom: 1px solid #cbd5e1; padding-bottom: 2mm; margin-bottom: 4mm; flex-shrink: 0; }
        .poster-runheader-title { font-weight: 700; font-size: var(--p-body); }
        .poster-runheader-eyebrow { font-size: var(--p-small); text-transform: uppercase; letter-spacing: 0.14em; color: #94a3b8; }
        .poster-footer { flex-shrink: 0; display: flex; align-items: center; justify-content: space-between; gap: 4mm; border-top: 1px solid #cbd5e1; padding-top: 2.5mm; margin-top: 3mm; font-size: var(--p-small); color: #64748b; }

        @media print {
          @page { size: ${profile.widthMm}mm ${profile.heightMm}mm; margin: 0; }
          html, body { background: #fff !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .plakat-root { background: #fff !important; }
          .plakat-toolbar, .plakat-screen-note, .plakat-toolbar-meta, .plakat-measure { display: none !important; }
          .plakat-stage { padding: 0 !important; margin: 0 !important; gap: 0 !important; overflow: visible !important; display: block !important; }
          .poster-page { box-shadow: none !important; border: 0 !important; break-after: page; }
          .poster-page:last-child { break-after: auto; }
        }
      `}</style>
    </div>
  );
}
